import React, { useEffect, useMemo, useState } from 'react';
import { List } from 'lucide-react';

/**
 * Auto-generated Table of Contents for long blog posts.
 *
 * Parses the rendered HTML for h2 / h3 headings, assigns ids if missing,
 * and renders an anchor-link list. Only shows when blog content is
 * ≥1500 words OR ≥3 H2 headings (per user's Phase 2 spec).
 *
 * Mount this AFTER the blog content is rendered to the DOM.
 */
export default function TableOfContents({ contentSelector = '.blog-content' }) {
  const [headings, setHeadings] = useState([]);

  useEffect(() => {
    const root = document.querySelector(contentSelector);
    if (!root) return;

    // Word-count gate
    const words = (root.innerText || '').trim().split(/\s+/).length;
    const h2List = Array.from(root.querySelectorAll('h2, h3'));
    if (words < 1500 && h2List.length < 3) {
      setHeadings([]);
      return;
    }

    const items = h2List.map((node, idx) => {
      // Generate stable id if missing
      if (!node.id) {
        const base = (node.textContent || `section-${idx}`)
          .toLowerCase()
          .replace(/[^a-z0-9\u0900-\u097F\s]/g, '')
          .trim()
          .replace(/\s+/g, '-')
          .slice(0, 50);
        node.id = base || `section-${idx}`;
      }
      return {
        id: node.id,
        text: node.textContent || '',
        level: parseInt(node.tagName[1], 10),
      };
    });
    setHeadings(items);
  }, [contentSelector]);

  if (headings.length === 0) return null;

  return (
    <nav className="mb-8 rounded-2xl bg-bg-card border border-gold/30 p-5" aria-label="Table of contents" data-testid="blog-toc">
      <div className="flex items-center gap-2 mb-3">
        <List className="w-4 h-4 text-gold" />
        <h2 className="text-sm font-bold tracking-[0.2em] uppercase text-gold m-0">Table of Contents</h2>
      </div>
      <ol className="space-y-1.5 list-none p-0 m-0">
        {headings.map((h) => (
          <li key={h.id} className={h.level === 3 ? 'pl-5' : ''}>
            <a
              href={`#${h.id}`}
              className="text-sm text-text-muted hover:text-gold transition-colors"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById(h.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
            >
              {h.text}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}
